import React, { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import type { NodeProps } from '@xyflow/react';
import { STATUS_CATEGORY_COLORS } from '../../types/playbook.types';
import type { StatusNodeData } from '../../hooks/usePlaybookEditor';

const StatusNode: React.FC<NodeProps> = memo(({ data, selected }) => {
  const d = data as StatusNodeData;
  const colors = STATUS_CATEGORY_COLORS[d.category] ?? STATUS_CATEGORY_COLORS.todo;

  return (
    <div
      className={`rounded-lg border-2 px-4 py-3 min-w-[160px] max-w-[220px] shadow-sm transition-shadow ${
        selected
          ? 'ring-2 ring-lex-accent ring-offset-1 shadow-md dark:ring-offset-gray-900'
          : 'hover:shadow-md'
      } ${colors.bg} ${colors.border} ${colors.darkBg} ${colors.darkBorder}`}
    >
      <Handle
        type="target"
        position={Position.Top}
        className="!w-3 !h-3 !bg-gray-400 !border-2 !border-white dark:!border-gray-800 !-top-1.5"
      />

      {/* Header row */}
      <div className="flex items-center gap-1.5 mb-1">
        {d.is_initial && (
          <span className="flex h-4 w-4 items-center justify-center rounded-full bg-lex-accent text-[8px] font-bold text-white shrink-0" title="Initial Status">
            S
          </span>
        )}
        {d.is_terminal && (
          <span className="flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500 text-[8px] font-bold text-white shrink-0" title="Terminal Status">
            E
          </span>
        )}
        <span className={`text-xs font-semibold truncate ${colors.text} ${colors.darkText}`}>
          {d.label}
        </span>
      </div>

      {/* Category badge */}
      <div className="flex items-center gap-1.5">
        <span className={`h-1.5 w-1.5 rounded-full ${colors.dot}`} />
        <span className="text-[9px] text-gray-500 dark:text-gray-400 uppercase tracking-wider">
          {d.category.replace('_', ' ')}
        </span>
      </div>

      {/* SLA indicator */}
      {d.sla_hours != null && d.sla_hours > 0 && (
        <div className="mt-1 text-[9px] text-amber-600 dark:text-amber-400">
          SLA: {d.sla_hours}h
        </div>
      )}

      <Handle
        type="source"
        position={Position.Bottom}
        className="!w-3 !h-3 !bg-gray-400 !border-2 !border-white dark:!border-gray-800 !-bottom-1.5"
      />
    </div>
  );
});

StatusNode.displayName = 'StatusNode';

export default StatusNode;
